import { useAuthCache } from '../contexts/AuthCacheContext';
import { login as localLogin, register as localRegister, clearToken } from '../lib/localAuth';

interface AuthState {
  user: {
    id: string;
    email: string;
    displayName?: string;
  } | null;
  loading: boolean;
}

export function useSafeAuth(): AuthState {
  const { user, loading } = useAuthCache();
  
  return {
    user: user && user.id ? {
      id: user.id,
      email: user.email,
      displayName: user.displayName,
    } : null,
    loading,
  };
}

// Re-export other auth-related functions that were in the original file
export { db } from '../lib/db';

// Add helper functions that some components might be using
export async function signIn(email: string, password: string) {
  try {
    const res = await localLogin(email, password);
    // Trigger auth cache refresh
    window.dispatchEvent(new Event('localAuthStateChange'));
    return { error: null, user: res.user };
  } catch (error: any) {
    return { error: { message: error.message || 'Failed to sign in' }, user: null };
  }
}

export async function signUp(email: string, password: string, displayName?: string) {
  try {
    const res = await localRegister(email, password);
    window.dispatchEvent(new Event('localAuthStateChange'));
    return { error: null, user: res.user };
  } catch (error: any) {
    return { error: { message: error.message || 'Failed to sign up' }, user: null };
  }
}

export async function sendPasswordResetEmail(_email: string): Promise<void> {
  // Not implemented for local auth yet
  throw new Error('Password reset not available on local auth');
}

export async function signOut(): Promise<void> {
  const { clearAuthContextCache } = await import('../lib/authContext');
  const { clearDbUserCache } = await import('./useDbUser');
  clearToken();
  clearAuthContextCache();
  clearDbUserCache();
  window.dispatchEvent(new Event('localAuthStateChange'));
  window.location.href = '/login';
}
